import RdnaSmartTable, { RdnaSmartTableRef } from '@ringdna/common/src/components/RdnaTable/RdnaSmartTable'
import RdnaCheckbox from '@ringdna/common/src/components/RdnaFormElements/RdnaCheckbox'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Column } from 'react-table'
import { SFUser, useSalesforceUsers } from '../../../../store/api/users-api'
import {
  COLUMN_WIDTH,
  getPaginationProps,
  NO_RESULT_STATE,
  PAGE_SIZE,
  RtnPagination,
  SERVER_PAGE_OFFSET
} from '../utils'
import RdnaApiAutoComplete, {
  AutoCompleteApiData
} from '@ringdna/common/src/components/RdnaFormElements/RdnaApiAutoComplete'
import styled from 'styled-components'

type Props = {
  userList: SFUser[]
  setUserList: (list: SFUser[]) => void
}

export default function AddNewUsers({ userList, setUserList }: Props) {
  const usersTableRef = useRef<RdnaSmartTableRef | undefined>()
  const totalRowsRef = useRef(0)
  const [profile, setProfile] = useState<AutoCompleteApiData | undefined>()
  const [displayLicensed, setDisplayLicensed] = useState<boolean>(false)

  const [queryState, setQueryState] = useState<RtnPagination>({
    globalFilter: '',
    sortBy: [],
    pageIndex: 0,
    pageSize: PAGE_SIZE
  })
  const [payload, , loading] = useSalesforceUsers({
    query: {
      profileId: profile?.value,
      globalFilter: queryState.globalFilter,
      displayLicensed: displayLicensed,
      'pager.pageSize': queryState.pageSize,
      'pager.currentPage': queryState.pageIndex + SERVER_PAGE_OFFSET,
      'pager.sortBy': queryState.sortBy[0]?.id || 'name',
      'pager.sortDirection': queryState.sortBy[0]?.desc ? 'desc' : 'asc'
    }
  })
  if (payload?.total) totalRowsRef.current = payload?.total

  const handleQueryState = useCallback(
    (state: RtnPagination) => {
      setQueryState(state)
    },
    [setQueryState]
  )

  const memoPayload = useMemo(() => {
    return payload?.results?.map((user: SFUser) => ({
      ...user,
      adminLicense: false,
      dialerLicense: false,
      calendarLicense: false,
      gsLicense: false,
      caiLicense: false,
      momentsLicense: false,
      revenueAILicense: false,
      areaCode: '',
      extensionCode: ''
    }))
  }, [payload])

  const check = (headerProp: { data: SFUser[] }) => {
    const data = headerProp.data.filter(user => !user.userId)
    const selected = userList.map(u => u.salesforceId)
    return data.length > 0 && data.every(u => selected.includes(u.salesforceId))
  }

  const columns = useMemo<Column<SFUser>[]>(
    () =>
      [
        {
          accessor: 'salesforceId',
          width: COLUMN_WIDTH,
          disableSortBy: true,
          Header: function headerCell(headerProp: { data: SFUser[] }) {
            return (
              <RdnaCheckbox
                onChange={(checked: boolean) => {
                  const data = headerProp.data.map(u => u.salesforceId)
                  checked
                    ? setUserList([
                        ...userList.filter(u => !data.includes(u.salesforceId)),
                        ...headerProp.data.filter(user => !user.userId)
                      ])
                    : setUserList([...userList.filter(u => !data.includes(u.salesforceId))])
                }}
                value={check(headerProp)}
              />
            )
          },
          Cell: function selectUserCell(row: { row: { original: SFUser } }) {
            return (
              <RdnaCheckbox
                onChange={(checked: boolean) => {
                  checked
                    ? setUserList([...userList.values(), row.row.original])
                    : setUserList(userList.filter(user => user.salesforceId !== row.row.original.salesforceId))
                }}
                value={userList.find(user => user.salesforceId === row.row.original.salesforceId) != null}
                disabled={!!row.row.original.userId}
              />
            )
          }
        },
        { Header: 'Name', accessor: 'name', width: COLUMN_WIDTH },
        { Header: 'Email', accessor: 'email', className: 'break-column', width: COLUMN_WIDTH },
        { Header: 'Title', accessor: 'title', width: COLUMN_WIDTH },
        { Header: 'City', accessor: 'city', width: COLUMN_WIDTH },
        { Header: 'Country', accessor: 'country', width: COLUMN_WIDTH },
        {
          Header: 'SFDC Profile',
          accessor: 'profile',
          disableFilters: false,
          width: COLUMN_WIDTH,
          Filter: function fn() {
            return (
              <RdnaApiAutoComplete
                name="profile-filter"
                queryName={'profile'}
                placeholder={'Salesforce Profile'}
                onChange={event => {
                  setProfile({ text: event.text, value: event.value })
                  usersTableRef.current?.gotoPage(0)
                }}
                searchUrl={`/api/v2/app/users/salesforce/picklist`}
                defaultSearchTerm={profile?.text}
                onUpdateSearch={event => {
                  if (!event.currentTarget.value) {
                    setProfile(undefined)
                    usersTableRef.current?.gotoPage(0)
                  }
                }}
              />
            )
          }
        },
        {
          id: 'display-licensed',
          accessor: user => user,
          disableFilters: false,
          disableSortBy: true,
          width: 0,
          minWidth: 0,
          Cell: function createdCell(data: { value: SFUser }) {
            return <div className={data.value.userId ? 'licensed' : ''}></div>
          },
          Filter: function fn() {
            return (
              <RdnaCheckbox
                onChange={value => {
                  setDisplayLicensed(value)
                  usersTableRef.current?.gotoPage(0)
                }}
                value={displayLicensed}
                label={'Display Licensed Users'}
              />
            )
          }
        }
      ] as Column<SFUser>[],
    [check, displayLicensed, profile?.text, setUserList, userList]
  )

  return (
    <Container>
      <RdnaSmartTable
        ref={usersTableRef}
        searchPlaceholder="Search"
        initialTableState={{
          globalFilter: '',
          sortBy: [],
          pageIndex: 0,
          pageSize: PAGE_SIZE
        }}
        columns={columns}
        data={memoPayload || []}
        noResultState={NO_RESULT_STATE}
        setQueryState={handleQueryState}
        isFetching={!!loading}
        totalItems={totalRowsRef?.current}
        {...getPaginationProps(memoPayload, totalRowsRef?.current)}
      />
    </Container>
  )
}

const Container = styled.div`
  [role='row']:has(.licensed) {
    filter: opacity(0.5);
  }
  [role='cell']:first-child,
  [role='columnheader']:first-child {
    max-width: ${COLUMN_WIDTH}px;
  }
`
